import { AbstractApi, ApiBaseResponse } from '../../../common/api'
import {
  MilestoneItem,
  Checklist,
  ChecklistDto,
  MilestoneTransferDto,
  MilestoneFilterTemplateDto,
  MilestoneOrder,
} from '../types/workflow.types'

class WorkflowApi extends AbstractApi {
  protected url = '/workflows'

  public async getUserActivitiesAndMilestones(): Promise<ApiBaseResponse<MilestoneItem[]>> {
    const { data: response } = await this.get<ApiBaseResponse<MilestoneItem[]>>(this.url)

    return response
  }

  public async getMilestone(id: number): Promise<MilestoneItem | null> {
    const { data: response } = await this.get<MilestoneItem | null>(this.url.concat(`/milestones/${id}`))

    return response
  }

  public async createMilestone(milestoneCreateData: Partial<MilestoneItem>): Promise<ApiBaseResponse<MilestoneItem>> {
    const { data: response } = await this.post<ApiBaseResponse<MilestoneItem>>(this.url.concat('/milestones'), milestoneCreateData)

    return response
  }

  public async updateMilestone(id: number, milestoneUpdateData: Omit<MilestoneItem, 'id'>): Promise<ApiBaseResponse<MilestoneItem>> {
    const { data: response } = await this.patch<ApiBaseResponse<MilestoneItem>>(this.url.concat(`/milestones/${id}`), milestoneUpdateData)

    return response
  }

  public async deleteMilestone(milestoneId: number): Promise<ApiBaseResponse<void>> {
    const { data: response } = await this.delete<ApiBaseResponse<void>>(this.url.concat(`/milestones/${milestoneId}`))

    return response
  }

  public async updateMilestoneSequence(milestoneList: MilestoneItem[]): Promise<ApiBaseResponse<void>> {
    const { data: response } = await this.put<ApiBaseResponse<void>>(this.url.concat('/milestones/sequence'), milestoneList)

    return response
  }

  public async createActivity(activityCreateData: Partial<MilestoneItem>): Promise<ApiBaseResponse<MilestoneItem>> {
    const { data: response } = await this.post<ApiBaseResponse<MilestoneItem>>(this.url.concat('/activities'), activityCreateData)

    return response
  }

  public async startChecklist(objectId: number, milestoneId: number): Promise<ApiBaseResponse<void>> {
    const { data: response } = await this.post<ApiBaseResponse<void>>(this.url.concat(`/checklist-start`), { objectId, milestoneId })

    return response
  }

  public async setChecklistResponsible(checklistId: number, responsibleEmail: string): Promise<ApiBaseResponse<void>> {
    const { data: response } = await this.post<ApiBaseResponse<void>>(this.url.concat(`/checklist/${checklistId}/set-responsible-by-email`), {
      email: responsibleEmail,
    })

    return response
  }

  public async getChecklist(objectId: number, milestoneId: number): Promise<ApiBaseResponse<ChecklistDto>> {
    const { data: response } = await this.get<ApiBaseResponse<ChecklistDto>>(this.url.concat('/checklist'), {
      duId: objectId,
      milestoneId: milestoneId,
    })

    return response
  }

  public async getAllChecklists(objectIds: number[]): Promise<ApiBaseResponse<ChecklistDto[]>> {
    const { data: response } = await this.post<ApiBaseResponse<ChecklistDto[]>>(this.url.concat('/checklist/all'), {
      duIds: objectIds,
    })

    return response
  }

  public async saveChecklist(parameters: Partial<ChecklistDto>): Promise<ApiBaseResponse<Checklist>> {
    const { data: response } = await this.post<ApiBaseResponse<Checklist>>(this.url.concat('/checklist'), parameters)

    return response
  }

  public async updateChecklist(id: number, objectId: number, milestoneId: number, parameters: any): Promise<ApiBaseResponse<Checklist>> {
    const { data: response } = await this.put<ApiBaseResponse<Checklist>>(this.url.concat(`/checklist/${id}`), {
      ...parameters,
      duId: objectId,
      milestoneId,
    })

    return response
  }

  public async transferMilestone(params: MilestoneTransferDto): Promise<ApiBaseResponse<boolean>> {
    const { data: response } = await this.post<ApiBaseResponse<boolean>>(this.url.concat('/milestone-transfer'), params)

    return response
  }

  public async saveFilterTemplate(dto: Omit<MilestoneFilterTemplateDto, 'id'>): Promise<ApiBaseResponse<boolean>> {
    const { data: response } = await this.post<ApiBaseResponse<boolean>>(this.url.concat('/template'), dto)

    return response
  }

  public async loadFilterTemplates(): Promise<ApiBaseResponse<MilestoneFilterTemplateDto[]>> {
    const { data: response } = await this.get<ApiBaseResponse<MilestoneFilterTemplateDto[]>>(this.url.concat('/template'))

    return response
  }

  public async deleteFilterTemplate(id: number): Promise<ApiBaseResponse<void>> {
    const { data: response } = await this.delete<ApiBaseResponse<void>>(this.url.concat(`/template/${id}`))

    return response
  }

  public async getLabelMilestoneSeq(labelId: number): Promise<ApiBaseResponse<any>> {
    const { data: response } = await this.get<ApiBaseResponse<any>>(this.url.concat(`/labels/${labelId}/milestone-sequence`))

    return response
  }

  public async updateLabelMilestoneSeq(labelId: number, milestoneSeq: MilestoneOrder[]): Promise<ApiBaseResponse<void>> {
    const { data: response } = await this.put<ApiBaseResponse<void>>(this.url.concat(`/labels/${labelId}/milestone-sequence`), milestoneSeq)

    return response
  }
}

export const workflowApi = new WorkflowApi()
